import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import employeeCategoryService from "../../services/EmployeeCategoryService";

export const getEmployeeCategoryList = createAsyncThunk(
  "employeeCategory/getEmployeeCategoryList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.GetEmployeeCategoryList();
      const raw = response.data?.ResultSet || response.data?.resultSet || [];

      return raw
        .filter((item) => item.HEC_IS_ACTIVE === true)
        .map((item) => ({
          id: String(item.HEC_EMPLOYMENT_CATEGORY_ID ?? ''),
          code: item.HEC_CATEGORY_CODE ?? '',
          name: item.HEC_CATEGORY_NAME ?? '',
          status: item.HEC_IS_ACTIVE ? 'Active' : 'Inactive',
        }));
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
        error.message ||
        "Failed to get employee category list"
      );
    }
  }
);

// Every category, active AND inactive, with all fields — for the Employment
// Types master screen. getEmployeeCategoryList stays active-only because the
// dropdowns elsewhere must not offer inactive categories.
export const getAllEmployeeCategories = createAsyncThunk(
  "employeeCategory/getAllEmployeeCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.GetEmployeeCategoryList();
      return response.data?.ResultSet || response.data?.resultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
        error.message ||
        "Failed to get employee category list"
      );
    }
  }
);

export const addEmployeeCategory = createAsyncThunk(
  "employeeCategory/addEmployeeCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.AddEmployeeCategory(payload);
      return response.data?.ResultSet || response.data?.resultSet || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
        error.message ||
        "Failed to add employee category"
      );
    }
  }
);

export const updateEmployeeCategory = createAsyncThunk(
  "employeeCategory/updateEmployeeCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.UpdateEmployeeCategory(payload);
      return response.data?.ResultSet || response.data?.resultSet || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
        error.message ||
        "Failed to update employee category"
      );
    }
  }
);

const initialState = {
  categories: [],
  allCategories: [],      // raw rows, active + inactive (master screen)
  allLoading: false,
  loading: false,
  saving: false,
  error: null,
  success: false,
};

const employeeCategorySlice = createSlice({
  name: "employeeCategory",
  initialState,
  reducers: {
    clearEmployeeCategoryError: (state) => {
      state.error = null;
    },
    clearEmployeeCategorySuccess: (state) => {
      state.success = false;
    },
    clearEmployeeCategories: (state) => {
      state.categories = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Employee Category List
      .addCase(getEmployeeCategoryList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmployeeCategoryList.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(getEmployeeCategoryList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load employee categories";
      })

      // Get All Employee Categories (active + inactive)
      .addCase(getAllEmployeeCategories.pending, (state) => {
        state.allLoading = true;
        state.error = null;
      })
      .addCase(getAllEmployeeCategories.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allCategories = action.payload || [];
      })
      .addCase(getAllEmployeeCategories.rejected, (state, action) => {
        state.allLoading = false;
        state.error = action.payload || "Failed to load employee categories";
      })

      // Add Employee Category
      .addCase(addEmployeeCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addEmployeeCategory.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(addEmployeeCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to add employee category";
      })

      // Update Employee Category
      .addCase(updateEmployeeCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateEmployeeCategory.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(updateEmployeeCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to update employee category";
      });
  },
});

export const {
  clearEmployeeCategoryError,
  clearEmployeeCategorySuccess,
  clearEmployeeCategories,
} = employeeCategorySlice.actions;

export const selectEmployeeCategories = (state) => state.employeeCategorySlice.categories;
export const selectEmployeeCategoryLoading = (state) => state.employeeCategorySlice.loading;
export const selectAllEmployeeCategories = (state) => state.employeeCategorySlice.allCategories;

export default employeeCategorySlice.reducer;